import { objectKeys } from "@/common/utils/object.utils";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import { FilterEntryWithoutLabel } from "@/common/contracts/ads.contracts";
import { useFiltering } from "@/components/Account/ads/hooks/useFiltering";

export const useAdsURLFiltering = <T extends string>(
  URLParam: string,
  defaultFilters: T[]
) => {
  const { query, isReady, push } = useRouter();

  const initialFiltering = useMemo(() => {
    const newFilters = defaultFilters.reduce<
      Partial<Record<T, FilterEntryWithoutLabel>>
    >((filterObj, filter) => {
      filterObj[filter] = {
        active: false,
      };

      return filterObj;
    }, {});

    if (!isReady || !query[URLParam]) {
      return newFilters;
    }

    const rawValues = Array.isArray(query[URLParam])
      ? query[URLParam]
      : [query[URLParam]];

    for (const rawValue of rawValues) {
      if (typeof rawValue !== "string") {
        continue;
      }

      if (!defaultFilters.includes(rawValue as T)) {
        continue;
      }

      const statusEntry = rawValue as T;

      newFilters[statusEntry] = {
        ...newFilters[statusEntry],
        active: true,
      };
    }

    return newFilters;
  }, [URLParam, defaultFilters, isReady, query]);

  const { filtering, onToggleFilter } = useFiltering<T>(initialFiltering);

  const filteringQueryValue = useMemo(() => {
    if (!filtering) {
      return;
    }

    const filteringKeys = objectKeys(filtering);
    const hasActiveFilter = filteringKeys.some(key => !!filtering[key]?.active);

    if (hasActiveFilter) {
      return filteringKeys.filter(key => filtering[key]?.active).join(",");
    }

    return filteringKeys.join(",");
  }, [filtering]);

  const handleToggleFilter = useCallback(
    (filter: T) => {
      onToggleFilter(filter);

      const newFilters: Partial<Record<T, FilterEntryWithoutLabel>> = {
        ...filtering,
        [filter]: {
          active: !filtering?.[filter]?.active,
        },
      };

      const activeKeys = objectKeys(newFilters).filter(
        key => !!newFilters[key]?.active
      );

      if (activeKeys.length === 0) {
        delete query[URLParam];
      } else {
        query[URLParam] = activeKeys;
      }

      // when adding/removing an filter, we want to reset the page to 1
      query.page = "1";

      push({ query }, undefined, { shallow: true });
    },
    [onToggleFilter, filtering, query, URLParam, push]
  );

  return {
    filtering,
    filteringQueryValue,
    onToggleFilter: handleToggleFilter,
  };
};
